import { motion } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { Check, Sparkles } from "lucide-react";
import { waLink } from "@/lib/brand";
import { useT } from "@/lib/i18n";

export default function Pricing() {
  const { t } = useT();
  const TIERS = [
    {
      id: "search",
      name: t("pri.t1.name"),
      tagline: t("pri.t1.tag"),
      includes: t("pri.t1.inc"),
      notIncluded: t("pri.t1.no"),
      best: false,
      msg: t("pri.t1.msg"),
    },
    {
      id: "search-docs",
      name: t("pri.t2.name"),
      tagline: t("pri.t2.tag"),
      includes: t("pri.t2.inc"),
      notIncluded: t("pri.t2.no"),
      best: true,
      msg: t("pri.t2.msg"),
    },
    {
      id: "full",
      name: t("pri.t3.name"),
      tagline: t("pri.t3.tag"),
      includes: t("pri.t3.inc"),
      notIncluded: t("pri.t3.no"),
      best: false,
      msg: t("pri.t3.msg"),
    },
  ];

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 bg-navy-800/40 overflow-hidden">
      <div className="absolute inset-x-0 top-0 divider-gold" />
      <div className="absolute -top-32 -right-32 h-[500px] w-[500px] border border-gold/10 rounded-full pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{t("pri.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight max-w-3xl">
              {t("pri.title.1")}{" "}
              <span className="italic text-gold">{t("pri.title.2")}</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md leading-relaxed">{t("pri.blurb")}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((tier, i) => (
            <motion.article
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`pricing-tier-${tier.id}`}
              className={`relative rounded-3xl p-8 md:p-9 flex flex-col transition-[border-color] duration-300 ${
                tier.best
                  ? "bg-gradient-to-b from-navy-700/80 to-navy-800 border-2 border-gold shadow-[0_30px_80px_-20px_rgba(201,162,75,0.35)] md:-top-4"
                  : "bg-navy-900/60 border border-gold/20 hover:border-gold/50"
              }`}
            >
              {tier.best && (
                <div
                  data-testid="pricing-best-badge"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-gold text-navy-900 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full whitespace-nowrap"
                >
                  <Sparkles className="h-3 w-3" /> {t("pri.best")}
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-serif text-2xl md:text-3xl text-white mb-2">{tier.name}</h3>
                <p className="text-sm text-gold-light italic">{tier.tagline}</p>
              </div>
              <div className="h-px bg-gold/15 mb-6" />
              <div className="flex-1 space-y-3 mb-6">
                <div className="text-[11px] tracking-[0.2em] text-gold uppercase mb-2">{t("pri.included")}</div>
                {(tier.includes || []).map((line) => (
                  <div key={line} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15 border border-gold/30">
                      <Check className="h-3 w-3 text-gold" />
                    </span>
                    <span className="text-sm text-slate-200 leading-relaxed">{line}</span>
                  </div>
                ))}
                {(tier.notIncluded || []).length > 0 && (
                  <>
                    <div className="text-[11px] tracking-[0.2em] text-slate-500 uppercase mt-5 mb-2">{t("pri.not_included")}</div>
                    {tier.notIncluded.map((line) => (
                      <div key={line} className="flex items-start gap-3 opacity-70">
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-600">
                          <span className="h-0.5 w-2 bg-slate-500" />
                        </span>
                        <span className="text-sm text-slate-500 leading-relaxed line-through decoration-slate-600">{line}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              <a
                href={waLink(tier.msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`pricing-cta-${tier.id}`}
                className={`mt-auto inline-flex items-center justify-center gap-2.5 font-semibold px-6 py-3.5 rounded-full transition-[color,background-color,border-color,box-shadow,transform] ${
                  tier.best
                    ? "bg-gold hover:bg-gold-light text-navy-900 shadow-[0_10px_30px_-8px_rgba(201,162,75,0.6)] hover:-translate-y-0.5"
                    : "border border-gold/50 text-gold hover:bg-gold hover:text-navy-900"
                }`}
              >
                <FaWhatsapp className="h-4 w-4" />
                {t("pri.cta")}
              </a>
            </motion.article>
          ))}
        </div>

        <p data-testid="pricing-guarantees" className="mt-12 text-center text-sm text-slate-400">
          {t("pri.guarantees")}
        </p>
      </div>
    </section>
  );
}
